// ts-rest contract for Comply > Fin-Crime & KYC.
// Composes the KycReview + EddCase Object Type rows behind one typed read.
// Per ADR-0018 the rows the server knows the shape of are real `z.object`
// schemas (the precise InferObject projection), not `z.record(z.unknown())`.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// The KycReview Object Type row (InferObject), projected field-by-field. The
// review carries no `clientName` ontology property (the Client link is the
// canonical relationship); resolvers MAY denormalise, but the contract
// projects only the modelled fields.
export const kycReviewDtoSchema = z.object({
    id: z.string(),
    riskTier: z.enum(['Conservative', 'Cautious', 'Balanced', 'Growth', 'Adventurous']),
    reviewPeriodMonths: z.number(),
    openedAt: z.string(),
    targetDueAt: z.string(),
    lastCompletedAt: z.string().nullable(),
    nextCycleDueAt: z.string().nullable(),
    status: z.enum(['Pending', 'AwaitingInfo', 'Escalated', 'Approved']),
    requestInfoNotes: z.string().nullable(),
    reviewerActorId: z.string().nullable(),
    escalationLevel: z.number(),
    escalatedToActorId: z.string().nullable(),
    escalatedAt: z.string().nullable(),
    escalationReason: z.string().nullable(),
});
export type KycReviewDto = z.infer<typeof kycReviewDtoSchema>;

// The EddCase Object Type row (InferObject), projected field-by-field.
export const eddCaseDtoSchema = z.object({
    id: z.string(),
    triggerReason: z.string(),
    triggerKind: z.enum([
        'SanctionsHit',
        'PepDetection',
        'UnusualActivity',
        'ScheduledHighRisk',
        'ExternalReferral',
        'Other',
    ]),
    riskAssessment: z.enum(['Low', 'Medium', 'High', 'VeryHigh']),
    openedAt: z.string(),
    nextReviewAt: z.string().nullable(),
    reviewerActorId: z.string(),
    findings: z.string().nullable(),
    status: z.enum(['Open', 'InProgress', 'Escalated', 'Completed']),
    outcome: z.enum(['Cleared', 'RestrictionsImposed', 'OffboardClient', 'ReportToFCA']).nullable(),
    resolvedAt: z.string().nullable(),
    resolutionNote: z.string().nullable(),
    escalationLevel: z.number(),
    escalatedToActorId: z.string().nullable(),
    escalatedAt: z.string().nullable(),
    escalationReason: z.string().nullable(),
    originFindingId: z.string().nullable(),
});
export type EddCaseDto = z.infer<typeof eddCaseDtoSchema>;

// The ComplianceAlert screening-feed row, projected onto the display
// vocabulary the Screening Alerts tab renders ("PEP Match", "Under Review",
// ...). The mapping seam (controller) translates the ontology PascalCase
// `alertType` / `status` enums via the shared compliance-alert-display map
// (cf. comply-dashboard.service.ts).
export const screeningAlertDtoSchema = z.object({
    id: z.string(),
    clientName: z.string(),
    alertType: z.enum(['PEP Match', 'Sanctions', 'Adverse Media', 'Sanctions: related entity']),
    source: z.string(),
    matchScore: z.number(),
    keyDetail: z.string(),
    dateDetected: z.string(),
    status: z.enum(['Open', 'Under Review', 'Dismissed', 'Confirmed']),
    reviewer: z.string(),
    detail: z.string().nullable(),
});
export type ScreeningAlertDto = z.infer<typeof screeningAlertDtoSchema>;

export const finCrimeKycResponseSchema = z.object({
    screeningAlerts: z.array(screeningAlertDtoSchema),
    kycReviews: z.array(kycReviewDtoSchema),
    eddCases: z.array(eddCaseDtoSchema),
});
export type FinCrimeKycResponse = z.infer<typeof finCrimeKycResponseSchema>;

const errorBody = z.object({ error: z.string() });
const okBody = z.object({ ok: z.literal(true) });

// Projection of the CloseEddCase Action's `outcome` enum (ADR-0008 write-side
// rule, #167) — the officer's closure decision, collected in the UI.
export const eddOutcomeSchema = z.enum([
    'Cleared',
    'RestrictionsImposed',
    'OffboardClient',
    'ReportToFCA',
]);
export type EddOutcomeDto = z.infer<typeof eddOutcomeSchema>;

export const complyFinCrimeKycContract = c.router(
    {
        get: {
            method: 'GET',
            path: '/comply/fin-crime-kyc',
            responses: { 200: finCrimeKycResponseSchema, 403: errorBody },
            summary: 'Composed Comply > Fin-Crime & KYC payload',
        },
        approveKycRenewal: {
            method: 'POST',
            path: '/comply/kyc/approve',
            // reviewId is the target KycReview id; approvalNote is the officer's
            // optional sign-off note, threaded onto the ApproveKycRenewal Action
            // input (ADR-0008 write-side rule).
            body: z.object({ reviewId: z.string(), approvalNote: z.string().optional() }),
            responses: { 200: okBody, 400: errorBody, 403: errorBody },
            summary: 'Approve an existing KYC review',
        },
        requestKycInfo: {
            method: 'POST',
            path: '/comply/kyc/:reviewId/request-info',
            pathParams: z.object({ reviewId: z.string() }),
            body: z.object({ notes: z.string().optional() }).optional(),
            responses: { 200: okBody, 400: errorBody, 403: errorBody },
            summary: 'Request additional KYC documentation',
        },
        escalateKyc: {
            method: 'POST',
            path: '/comply/kyc/:reviewId/escalate',
            pathParams: z.object({ reviewId: z.string() }),
            // escalationReason is the officer's real reason for escalating, threaded
            // onto the EscalateKycReview Action input (ADR-0008 write-side rule,
            // #167) — not a server constant.
            body: z.object({ escalationReason: z.string().min(1) }),
            responses: { 200: okBody, 400: errorBody, 403: errorBody },
            summary: 'Escalate a KYC review',
        },
        closeEddCase: {
            method: 'POST',
            path: '/comply/edd/:caseId/close',
            pathParams: z.object({ caseId: z.string() }),
            // outcome + resolutionNote are the officer's real closure decision,
            // threaded onto the CloseEddCase Action input (ADR-0008's named worked
            // example, #167) — not server constants.
            body: z.object({
                outcome: eddOutcomeSchema,
                resolutionNote: z.string().min(1),
            }),
            responses: { 200: okBody, 400: errorBody, 403: errorBody },
            summary: 'Close an EDD case with an outcome',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
